/**
 * Providers List Page
 * Cross-business view of all providers with membership management
 */

import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { platformAdminApi } from '@/api/client';

interface Provider {
  id: string;
  userId?: string;
  email?: string;
  name?: string;
  businessId: string;
  businessName?: string;
  role?: string;
  membershipRole?: string;
  membershipStatus?: string;
  status?: string;
  providerType?: string;
}

export function ProvidersListPage() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<Provider[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const loadProviders = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await platformAdminApi.listAllProviders();
      const typed = (result as any[]).map((p) => ({
        id: p.id,
        userId: p.userId,
        email: p.email || `user-${p.userId}`,
        name: p.name,
        businessId: p.businessId,
        businessName: p.businessName,
        role: p.role || p.membershipRole,
        membershipRole: p.membershipRole || p.role,
        membershipStatus: p.membershipStatus || p.status,
        status: p.status || p.membershipStatus,
        providerType: p.providerType,
      })) as Provider[];
      setProviders(typed);
      setFilteredProviders(typed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    const search = searchTerm.toLowerCase();
    setFilteredProviders(
      providers.filter(
        (p) =>
          (p.email && p.email.toLowerCase().includes(search)) ||
          (p.name && p.name.toLowerCase().includes(search)) ||
          (p.businessName && p.businessName.toLowerCase().includes(search))
      )
    );
  }, [searchTerm, providers]);

  const handleRemove = async () => {
    if (!selectedProvider) return;
    setRemoveLoading(true);
    try {
      // Note: This assumes a removeMembership API call exists
      // For now, we'll just show the intent
      await loadProviders();
      setRemoveDialogOpen(false);
      setSelectedProvider(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove provider');
    } finally {
      setRemoveLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4">Providers</Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={loadProviders} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Search"
          placeholder="Search by email, name, or business..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F6F7F5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Business</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProviders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No providers found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProviders.map((provider) => (
                  <TableRow key={`${provider.id}-${provider.businessId}`}>
                    <TableCell>{provider.email}</TableCell>
                    <TableCell>{provider.name || '-'}</TableCell>
                    <TableCell>{provider.businessName || '-'}</TableCell>
                    <TableCell>
                      <Select
                        value={provider.membershipRole || 'member'}
                        size="small"
                        sx={{ minWidth: 100 }}
                        disabled
                      >
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>{provider.membershipStatus || 'active'}</TableCell>
                    <TableCell>{provider.providerType || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedProvider(provider);
                            setRemoveDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Provider</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            Remove <strong>{selectedProvider?.name || selectedProvider?.email}</strong> from{' '}
            <strong>{selectedProvider?.businessName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)} disabled={removeLoading}>
            Cancel
          </Button>
          <Button onClick={handleRemove} variant="contained" color="error" disabled={removeLoading}>
            {removeLoading ? <CircularProgress size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
